/**
 * Drag (or click) along a bar to move through the video: pointer capture, so the drag keeps going outside the bar;
 * the playhead follows the pointer and the player seeks where it's released (vimeoClock.scrubTo). Touch works the
 * same way (touch-action: none on the bar).
 */
import { useEffect, type RefObject } from "react";
import { scrubTo } from "./vimeoClock";

export function useScrub(ref: RefObject<HTMLElement | null>, duration: number, range: { from: number; to: number } | null = null) {
  useEffect(() => {
    const el = ref.current;
    if (!el || !duration) return;
    const from = range?.from ?? 0, len = (range?.to ?? duration) - from;
    let dragging = false;
    const at = (clientX: number) => {
      const r = el.getBoundingClientRect();
      return from + Math.min(1, Math.max(0, (clientX - r.left) / r.width)) * len;
    };
    const down = (e: PointerEvent) => {
      if (e.button !== 0 || (e.target as HTMLElement).closest("button")) return;
      dragging = true;
      el.setPointerCapture(e.pointerId);
      el.dataset.scrubbing = "";
      scrubTo(at(e.clientX));
    };
    const move = (e: PointerEvent) => { if (dragging) scrubTo(at(e.clientX)); };
    const up = (e: PointerEvent) => {
      if (!dragging) return;
      dragging = false;
      delete el.dataset.scrubbing;
      scrubTo(at(e.clientX), true);
    };
    el.addEventListener("pointerdown", down);
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerup", up);
    el.addEventListener("pointercancel", up);
    return () => {
      el.removeEventListener("pointerdown", down);
      el.removeEventListener("pointermove", move);
      el.removeEventListener("pointerup", up);
      el.removeEventListener("pointercancel", up);
    };
  }, [ref, duration, range?.from, range?.to]);
}
